// The panel behind one app tab: the widget named in the page's route, framed the
// way every embedded widget is, or a line saying it is gone. The page owns the
// tab bar and the route; which widgets a tab slot shows is `useSlotEmbeds`', and
// the frame and its platform behaviour are `SlotContainer`'s.

import { t } from '@lingui/core/macro';
import type { SlotId } from '@luminovo/apps-types';
import {
    CenteredSpinner,
    colorSystem,
    Flexbox,
    stringifySearch,
    TertiaryButton,
    Text,
    useRouteMatches,
    useSearch,
} from '@luminovo/design-system';
import type { ReactElement } from 'react';

import { useSlotEmbedsState } from '../hooks/useSlotEmbeds';
import { SlotContainer } from './SlotContainer';

export interface AppTabPanelProps {
    slotId: SlotId;
    /** The app and widget the page's route names. */
    appId: string;
    widgetId: string;
    /** What this slot hands its widget, as the slot registry declares it. */
    params: Readonly<Record<string, unknown>>;
    /** Slug of the tenant the product page is showing, handed to the widget. */
    tenant: string;
    /** Locale used to resolve app-authored names, and handed to the widget. */
    locale: string;
}

export function AppTabPanel({ slotId, appId, widgetId, params, tenant, locale }: AppTabPanelProps): ReactElement {
    const { embeds, settled, degraded } = useSlotEmbedsState(slotId, params, locale);
    // The page around the slot. The tab's widget does not own the URL, but it is
    // told where the user is, like any other widget.
    const pathname = useRouteMatches().at(-1)?.pathname ?? '/';
    const search = useSearch({ strict: false, select: stringifySearch });

    const embed = embeds.find(
        (candidate) => candidate.assignment.app.id === appId && candidate.assignment.widget.id === widgetId,
    );

    if (embed === undefined) {
        if (!settled) {
            return <CenteredSpinner />;
        }
        // A link to an app that was deleted or renamed, or a draft tab for someone
        // who is not its maintainer, says so rather than sending the user elsewhere.
        return (
            <Text variant="body" color={colorSystem.neutral[7]}>
                {degraded ? t`App tabs could not load.` : t`This app tab is not available.`}
            </Text>
        );
    }

    const appName = embed.assignment.app.name;
    return (
        <SlotContainer
            key={embed.key}
            assignment={embed.assignment}
            appsOrigin={embed.appsOrigin}
            slotId={slotId}
            tenant={tenant}
            params={params}
            location={{ pathname, search, hash: '' }}
            locale={locale}
            navigation="embedded"
            errorFallback={(retry) => (
                <Flexbox gap={8} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                    <Text variant="body-small" color={colorSystem.neutral[7]}>
                        {t`${appName} could not load.`}
                    </Text>
                    <TertiaryButton size="small" onClick={retry}>
                        {t`Retry`}
                    </TertiaryButton>
                </Flexbox>
            )}
        />
    );
}
